import { Box, Typography, FormControlLabel, Checkbox, useTheme } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { System, Vehicle } from "../../state/types";
import { State } from '../../state/reducers';
import { useSelector } from "react-redux";

interface VehicleInfoSidebarProps {
  vehicle: Vehicle;
  visibleTypes: { [key: string]: boolean };
  setVisibleTypes: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
  imageCategories: { key: string; label: string; images: any[] }[];
}

const VehicleInfoSidebar: React.FC<VehicleInfoSidebarProps> = ({
  vehicle,
  visibleTypes,
  setVisibleTypes,
  imageCategories,
}) => {
  const theme = useTheme();
  const systems: System[] = useSelector((state: State) => state.systemState.systems)

  const handleCheckboxChange = (key: string) => {
    setVisibleTypes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getSyndicationStep = (vehicle: Vehicle) => {
    if (!vehicle || !vehicle.syndicationSteps || !vehicle.syndicationCurrentStep) return ''

    if (vehicle.syndicationCurrentStep === "COMPLETE") return `${vehicle?.syndicationSteps[0]} COMPLETE`

    return vehicle.syndicationCurrentStep
  }

  const getCommonNameForSystem = (systemCode: string) => {
    return systems?.find((sys: System) => sys.systemCode === systemCode)?.commonName
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        position: { xs: "relative", md: "sticky" },
        top: { md: "5px" },
        height: "100%",
        maxHeight: { xs: "auto", md: "calc(100vh - 120px)" },
        borderRight: { md: `5px solid ${theme.palette.divider}` },
        borderBottom: { xs: `5px solid ${theme.palette.divider}`, md: "none" },
        overflowY: "auto",
        zIndex: 2,
        p: 2
      }}
    >
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: "bold", mb: 2 }}>
        Vehicle Info
      </Typography>

      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Ran at:</strong> {new Date(+vehicle.created_at * 1000).toLocaleString()}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Asset ID:</strong> {vehicle.assetIdentifier}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Client ID:</strong> {vehicle.clientId}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Client Name:</strong> {getCommonNameForSystem(vehicle.clientId || "")}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Config Name:</strong> {vehicle.config}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Processing Status:</strong> {vehicle.processingCurrentStep?.toUpperCase()}
      </Typography>

      {vehicle.processingError && (
        <Box
          sx={{
            backgroundColor: theme.palette.error.main,
            color: "white",
            borderRadius: 1,
            py: 1,
            px: 2,
            mb: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <WarningAmberIcon sx={{ fontSize: "1.2rem" }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {vehicle.processingError}
          </Typography>
        </Box>
      )}

      <Typography variant="body2" sx={{ mb: 2 }}>
        <strong>Syndication Status:</strong> {getSyndicationStep(vehicle)}
      </Typography>

      {vehicle.syndicationError && (
        <Box
          sx={{
            backgroundColor: theme.palette.error.main,
            color: "white",
            borderRadius: 1,
            py: 1,
            px: 2,
            mb: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <WarningAmberIcon sx={{ fontSize: "1.2rem" }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {vehicle.syndicationError}
          </Typography>
        </Box>
      )}

      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
        Image Categories
      </Typography>

      {imageCategories.map(({ key, label, images }) => (
        <FormControlLabel
          key={key}
          control={
            <Checkbox
              checked={visibleTypes[key]}
              onChange={() => handleCheckboxChange(key)}
              disabled={!images.length}
              sx={{
                "&.Mui-checked": {
                  color: "darkred",
                },
              }}
            />
          }
          label={`${label} (${images.length})`}
          sx={{
            opacity: images.length ? 1 : 0.5,
          }}
        />
      ))}
    </Box>
  );
};

export default VehicleInfoSidebar;
